import { useMemo } from "react";
import { addDays, parseISO, todayISO } from "../lib/format";
import { cn } from "../lib/utils";

export const SITE_COLORS = ["bg-sky-600", "bg-emerald-600", "bg-teal-600", "bg-cyan-700", "bg-lime-700", "bg-blue-600", "bg-green-700"];
export const siteColor = (id) => SITE_COLORS[(id || 0) % SITE_COLORS.length];

export const Legend = () => (
  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground" data-testid="calendar-legend">
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-sky-600" />Assegnazione (colore per cantiere)</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-red-600" />Conflitto</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-orange-500" />Assenza</span>
    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-purple-600" />Indisponibile / manutenzione</span>
  </div>
);

const covers = (e, d) => (!e.start_date || e.start_date <= d) && (!e.end_date || e.end_date >= d);

const KIND_STYLE = {
  absence: ["bg-orange-500 text-slate-950", "Assente"],
  unavailable: ["bg-purple-600 text-white", "Manut."],
  conflict: ["bg-red-600 text-white", "CONFLITTO"],
};
const cellStyle = (e) => KIND_STYLE[e.kind] || [`${siteColor(e.site_id)} text-white`, e.site_name];

function compareRows(a, b) {
  if (a.type === b.type) return a.name.localeCompare(b.name);
  return a.type === "worker" ? -1 : 1;
}

function useTimelineRows(events, absences, unavailable) {
  return useMemo(() => {
    const map = new Map();
    const add = (e, key) => {
      if (!map.has(key)) map.set(key, { key, name: e.resource_name, type: e.resource_type, items: [] });
      map.get(key).items.push(e);
    };
    events.forEach((e) => add(e, `${e.resource_type}-${e.worker_id || e.vehicle_id}`));
    absences.forEach((e) => add(e, `worker-${e.worker_id}`));
    unavailable.forEach((e) => add(e, `vehicle-${e.vehicle_id}`));
    return [...map.values()].sort(compareRows);
  }, [events, absences, unavailable]);
}

function DayHeader({ d, today, onDayClick }) {
  const dt = parseISO(d);
  const weekend = dt.getDay() === 0 || dt.getDay() === 6;
  return (
    <th onClick={() => onDayClick?.(d)} className={cn("p-1.5 border-b border-border font-medium cursor-pointer hover:text-white", weekend && "text-slate-500", d === today && "text-amber-400")}>
      <div className="capitalize">{dt.toLocaleDateString("it-IT", { weekday: "short" })}</div>
      <div className="num">{dt.getDate()}</div>
    </th>
  );
}

function TimelineCell({ row, d, today, compact }) {
  const e = row.items.find((x) => covers(x, d));
  const [cls, label] = e ? cellStyle(e) : ["", ""];
  return (
    <td className={cn("border-b border-border/50 p-0.5", d === today && "bg-amber-500/5")}>
      {e && <div className={cn("rounded px-1.5 py-1 truncate font-medium", cls)} title={`${row.name} · ${label}`}>{compact ? "" : label}&nbsp;</div>}
    </td>
  );
}

function TimelineRow({ row, dates, today, compact }) {
  return (
    <tr data-testid={`timeline-row-${row.key}`}>
      <td className="sticky left-0 z-10 bg-card p-2 border-b border-border/50 truncate max-w-[200px]">
        <span className={cn("inline-block h-2 w-2 rounded-full mr-2", row.type === "worker" ? "bg-amber-400" : "bg-sky-400")} />{row.name}
      </td>
      {dates.map((d) => <TimelineCell key={d} row={row} d={d} today={today} compact={compact} />)}
    </tr>
  );
}

export default function Timeline({ events = [], absences = [], unavailable = [], start, days = 7, onDayClick }) {
  const dates = useMemo(() => Array.from({ length: days }, (_, i) => addDays(start, i)), [start, days]);
  const rows = useTimelineRows(events, absences, unavailable);
  const today = todayISO();
  const compact = days > 10;
  if (!rows.length) return <div className="py-10 text-center text-muted-foreground text-sm">Nessuna assegnazione nel periodo selezionato.</div>;
  return (
    <div className="overflow-x-auto scrollbar-thin" data-testid="timeline">
      <table className="w-full text-xs border-separate border-spacing-0" style={{ minWidth: 200 + days * (compact ? 38 : 90) }}>
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-card text-left p-2 w-[200px] border-b border-border">Risorsa</th>
            {dates.map((d) => <DayHeader key={d} d={d} today={today} onDayClick={onDayClick} />)}
          </tr>
        </thead>
        <tbody>{rows.map((r) => <TimelineRow key={r.key} row={r} dates={dates} today={today} compact={compact} />)}</tbody>
      </table>
    </div>
  );
}
